// Entry point: Zod without eval (strict CSP), deletes the browser-side Assistant thread store of earlier versions, remembers script errors for feedback reports, watches for new releases (a reload offer once the site serves a newer build), loads /config.json, configures the API client, mounts theme, query, auth (sign-out navigates the router to /signin) and router providers, and renders the app into one React root that survives hot module replacement.
import "@/lib/zod-config";

import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode, useEffect } from "react";
import { createRoot, type Root } from "react-dom/client";

import { configureApiClient, setInvalidResponseHandler } from "@/api/client";
import { AuthProvider } from "@/app/auth/AuthProvider";
import { useAuth } from "@/app/auth/useAuth";
import { loadRuntimeConfig, RuntimeConfigContext, type RuntimeConfig } from "@/app/config";
import { installErrorCapture } from "@/app/diagnostics";
import { CONTRACT_ERROR_GAP_MS, watchReleases } from "@/app/release";
import { ThemeProvider } from "@/app/theme/ThemeProvider";
import { purgeLegacyThreadStore } from "@/features/assistant/legacy-store";
import { TooltipProvider } from "@/components/ui/tooltip";
import { createQueryClient } from "@/query-client";
import { createAppRouter } from "@/router";

import "@/styles/app.css";

installErrorCapture();
const queryClient = createQueryClient();
const router = createAppRouter(queryClient);
const releases = watchReleases({ history: router.history });
setInvalidResponseHandler(() => void releases.check(CONTRACT_ERROR_GAP_MS));

function navigateToSignIn(): Promise<void> {
  return router.navigate({ to: "/signin", replace: true });
}

function AppRouter() {
  const auth = useAuth();
  useEffect(() => {
    void router.invalidate();
  }, [auth.status]);
  if (auth.status === "loading") {
    return null;
  }
  return <RouterProvider router={router} context={{ queryClient, auth }} />;
}

function BootError({ message }: { message: string }) {
  return (
    <main id="main" className="grid min-h-dvh place-items-center bg-background p-6 text-center">
      <div>
        <h1 className="m-0 text-h1 font-bold">The app could not start</h1>
        <p className="m-0 mt-2 text-sm text-muted-foreground">{message}</p>
      </div>
    </main>
  );
}

function appRoot(container: HTMLElement): Root {
  const hotData = import.meta.hot?.data as { root?: Root } | undefined;
  if (hotData?.root !== undefined) {
    return hotData.root;
  }
  const root = createRoot(container);
  if (hotData !== undefined) {
    hotData.root = root;
  }
  return root;
}

async function boot(): Promise<void> {
  const container = document.getElementById("root");
  if (container === null) {
    return;
  }
  const root = appRoot(container);
  void purgeLegacyThreadStore();
  let config: RuntimeConfig;
  try {
    config = await loadRuntimeConfig();
  } catch (error) {
    root.render(
      <BootError message={error instanceof Error ? error.message : "Invalid runtime configuration."} />,
    );
    return;
  }
  configureApiClient(config.apiBaseUrl);
  root.render(
    <StrictMode>
      <RuntimeConfigContext value={config}>
        <ThemeProvider>
          <QueryClientProvider client={queryClient}>
            <AuthProvider config={config.auth} onSignedOut={navigateToSignIn}>
              <TooltipProvider>
                <AppRouter />
              </TooltipProvider>
            </AuthProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </RuntimeConfigContext>
    </StrictMode>,
  );
}

void boot();
